"use client";

import {useState, useSyncExternalStore} from "react";
import {Link2, Share2} from "lucide-react";
import {Button} from "@/components/ui/button";

const subscribeNothing = () => () => {};

/**
 * "Copy link" and (where the browser has it) "Share" for the address in the address
 * bar, which carries every filter. Nothing about the listings is sent anywhere.
 */
export default function ShareButtons({title}: { title: string }) {
    // False while rendering on the server and hydrating, then true where the browser can share.
    const canShare = useSyncExternalStore(subscribeNothing, () => typeof navigator.share === "function", () => false);

    // The result is shown in the button and announced; no toast, which this layout doesn't mount.
    const [note, setNote] = useState("");

    async function copy() {
        try {
            await navigator.clipboard.writeText(window.location.href);
            setNote("Link copied");
        } catch {
            setNote("Couldn't copy: use the address bar");
        }
        setTimeout(() => setNote(""), 4000);
    }

    async function share() {
        try {
            await navigator.share({title, url: window.location.href});
        } catch {
            // dismissed by the visitor: nothing to report
        }
    }

    return (
        <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={copy}>
                <Link2 className="size-4" aria-hidden="true"/> Copy link
            </Button>
            <span role="status" className="self-center text-sm text-muted-foreground">{note}</span>
            {canShare ? (
                <Button type="button" variant="outline" size="sm" onClick={share}>
                    <Share2 className="size-4" aria-hidden="true"/> Share
                </Button>
            ) : null}
        </div>
    );
}
